'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useAuthContext } from '@/components/AuthProvider';
import { useLanguage } from '@/components/LanguageProvider'; 
import {
  Home, Map as MapIcon, ShoppingBag, Shield, Users, 
  ChevronLeft, LogIn, X, ExternalLink, Activity
} from 'lucide-react';

interface SidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  isMobileOpen: boolean;
  closeMobile: () => void;
}

export default function Sidebar({ isCollapsed, toggleSidebar, isMobileOpen, closeMobile }: SidebarProps) {
  const pathname = usePathname();
  const { openLoginModal, isAuthenticated, isLoading } = useAuthContext();
  const { t } = useLanguage(); 

  const localeMatch = pathname.match(/^\/([a-zA-Z]{2})(?=\/|$)/);
  const currentLocale = localeMatch ? localeMatch[1] : '';

  const pathWithoutLocale = pathname.replace(/^\/[a-zA-Z]{2}(?=\/|$)/, '') || '/';

  const menuGroups = [
    {
      title: t.sidebar.groups.main,
      items: [
        { icon: Home, label: t.sidebar.items.home, href: '/', hideInCollapsed: false },
        { icon: Activity, label: t.sidebar.items.stats, href: '/stats', hideInCollapsed: false },
        { icon: MapIcon, label: t.sidebar.items.map, href: '/map', hideInCollapsed: false }
      ]
    },
    {
      title: t.sidebar.groups.community,
      items: [
        { icon: Shield, label: t.sidebar.items.clans, href: '/clans', hideInCollapsed: true },
        { icon: Users, label: t.sidebar.items.friends, href: '/friends', hideInCollapsed: false },
        { icon: ShoppingBag, label: t.sidebar.items.shop, href: '/shop', badge: t.sidebar.badges.sale, hideInCollapsed: false },
      ]
    },
    {
      title: t.sidebar.groups.info,
      items: [
        { icon: ExternalLink, label: t.sidebar.items.wiki, href: 'https://rpwiki.royolands.com/', hideInCollapsed: false },
        { icon: ExternalLink, label: t.sidebar.items.support, href: 'https://dsc.gg/rplands', hideInCollapsed: false },
        { icon: ExternalLink, label: t.sidebar.items.rules, href: 'https://rpwiki.royolands.com/pravila/pravila-servera-rplands', hideInCollapsed: false },
      ]
    }
  ];

  return (
    <>
      <div
        onClick={closeMobile}
        className={`fixed inset-0 z-[60] bg-black/60 transition-opacity duration-300 md:hidden ${isMobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      />

      <aside 
        className={`
          fixed top-0 left-0 z-[70] h-full bg-[#121212] border-r border-white/5
          flex flex-col transition-all duration-300
          w-[280px]
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          ${isCollapsed ? 'md:w-[80px]' : 'md:w-[280px]'}
        `}
      >
        <button 
          onClick={toggleSidebar}
          className={`
            hidden md:flex items-center justify-center
            absolute -right-3 top-8 z-50 
            w-6 h-6 rounded-md 
            bg-[#1e1e1e] border border-white/10 
            text-zinc-400 hover:text-white hover:bg-[#2a2a2a] hover:border-white/20
            transition-all
          `}
        >
          <ChevronLeft size={14} className={`transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <button onClick={closeMobile} className="md:hidden absolute right-4 top-6 text-zinc-400 hover:text-white">
           <X size={24} />
        </button>

        <div className={`
            h-20 flex items-center shrink-0 border-b border-white/5 transition-all duration-300
            px-6 ${isCollapsed ? 'md:justify-center md:px-0' : 'md:justify-start md:px-6'}
        `}>
          <Link 
            href={currentLocale ? `/${currentLocale}` : '/'} 
            onClick={closeMobile} 
            className={`relative flex items-center h-8 transition-all duration-300 ${isCollapsed ? 'md:w-8' : 'w-[110px]'}`}
          >
             <Image 
               src="/logo.png" 
               alt="Logo" 
               width={110} 
               height={32} 
               className={`absolute left-0 object-contain transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:invisible' : 'opacity-100 visible'}`} 
               unoptimized 
             />
             <Image 
               src="https://webstatic.royolands.com/upload/contentweb/rpmini-logo.png" 
               alt="Mini Logo" 
               width={32} 
               height={32} 
               className={`absolute left-1/2 -translate-x-1/2 object-contain transition-opacity duration-200 ${isCollapsed ? 'md:opacity-100 md:visible' : 'opacity-0 invisible'}`} 
               unoptimized 
             />
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 space-y-8 scrollbar-hide">
          {menuGroups.map((group, idx) => (
            <div key={idx} className="px-3">
              <h3 className={`
                px-3 text-xs font-semibold text-zinc-500 uppercase mb-3 transition-opacity duration-300
                whitespace-nowrap overflow-hidden
                ${isCollapsed ? 'md:opacity-0 md:h-0 md:mb-0' : 'opacity-100'}
              `}>
                {group.title}
              </h3>
              
              <div className="space-y-1">
                {group.items.map((item) => {
                  if (isCollapsed && item.hideInCollapsed) return null;

                  const isExternal = item.href.startsWith('http');
                  const isActive = !isExternal && pathWithoutLocale === item.href;

                  let finalHref = item.href;
                  if (!isExternal && currentLocale) {
                    finalHref = item.href === '/' 
                      ? `/${currentLocale}` 
                      : `/${currentLocale}${item.href}`;
                  }
                  
                  return (
                    <Link
                      key={item.href}
                      href={finalHref}
                      onClick={closeMobile}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      className={`
                        relative flex items-center h-11 rounded-xl transition-all duration-200 group w-full px-3 gap-3
                        border
                        ${isActive 
                          ? 'bg-[#2a2a2a] text-white border-white/20' 
                          : 'text-zinc-400 border-transparent hover:bg-[#1e1e1e] hover:border-white/20 hover:text-zinc-200'}
                        ${isCollapsed ? 'md:justify-center md:px-0' : 'md:justify-start'}
                      `}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <item.icon 
                        size={20} 
                        className={`shrink-0 ${isActive ? 'text-indigo-400' : 'text-zinc-500 group-hover:text-zinc-300'}`} 
                      />
                      
                      <span className={`
                        font-medium whitespace-nowrap overflow-hidden
                        ${isCollapsed ? 'md:hidden' : 'block'}
                      `}>
                        {item.label}
                      </span>
                      
                      {item.badge && (
                        <>
                          <span className={`
                            ml-auto text-[11px] font-medium px-2 py-0.5 rounded-md
                            bg-red-500/10 text-red-400 border border-red-500/20
                            ${isCollapsed ? 'md:hidden' : 'block'}
                          `}>
                            {item.badge}
                          </span>
                          
                          {isCollapsed && (
                            <span className="hidden md:block absolute top-2 right-2 w-1.5 h-1.5 bg-red-500 rounded-full" />
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {!isAuthenticated && !isLoading && (
           <div className="p-4 border-t border-white/5 bg-[#121212]">
              <button
                 onClick={openLoginModal}
                 className={`
                   flex items-center rounded-xl bg-white text-black font-semibold hover:bg-zinc-200 transition-colors
                   h-11 w-full px-4 gap-2
                   ${isCollapsed ? 'md:justify-center md:px-0' : 'md:justify-start'}
                 `}
                 title={t.sidebar.login}
              >
                 <LogIn size={20} className="shrink-0" />
                 <span className={`whitespace-nowrap ${isCollapsed ? 'md:hidden' : 'block'}`}>
                    {t.sidebar.login}
                 </span>
              </button>
           </div>
        )}
      </aside>
    </>
  );
}